import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { OptionSelect, SearchSelect } from '../search-select/search-select';
import {
  AddItemsData,
  Item,
  ItemSelected,
  ItemSelectorResult,
} from '../../../core/models/selectordialog.interface';
import { unityOptions } from '../../../features/pages/consumableasset/consumableasset-form/comsumableasset.definition';

@Component({
  selector: 'app-item-selector-dialog',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    SearchSelect,
  ],
  templateUrl: './item-selector-dialog.html',
  styleUrl: './item-selector-dialog.scss',
})
export class ItemSelectorDialog {
  title!: string;
  labelSearch!: string;
  labelItem!: string;
  labelValue!: string;
  money!: string;

  // Lista completa que llega del api.
  listaItems: Item[] = [];

  // Opciones para el search-select (value + label).
  options: OptionSelect[] = [];

  // Item seleccionado en el search-select sin agregarse a la tabla.
  itemSelected: Item | null = null;

  // Items agregados a la tabla.
  items: ItemSelected[] = [];

  showUnity = false;
  showPackage = false;
  showUnityColumn = false;
  showValueColumn = false;

  readonly unityOptions = unityOptions;

  columnas: string[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) private readonly data: AddItemsData,
    private readonly dialogRef: MatDialogRef<ItemSelectorDialog, ItemSelectorResult>,
  ) {
    this.title = this.data.title ?? 'Agregar ítems';
    this.labelSearch = this.data.labelSearch ?? 'Buscar';
    this.labelItem = this.data.labelItem ?? 'Nombre';
    this.labelValue = this.data.labelValue ?? 'Valor';
    this.money = this.data.money ?? '$';
    this.showUnity = this.data.showUnity ?? false;
    this.showPackage = this.data.showPackage ?? false;
    this.showUnityColumn = this.data.showUnityColumn ?? false;
    this.showValueColumn = this.data.showValueColumn ?? false;
    this.columnas = [
      'acciones',
      'name',
      'value',
      'qty',
      ...(this.showPackage ? ['package'] : []),
      ...(this.showUnity || this.showUnityColumn ? ['unity'] : []),
      'total',
    ];
    this.items = (this.data.itemsIniciales ?? []).map((i) => ({ ...i }));
  }

  ngOnInit(): void {
    if (this.data.itemsDisponibles?.length) {
      this.listaItems = this.data.itemsDisponibles;
      this.options = this.listaItems.map((i) => ({
        value: i.id,
        label: i.name,
      }));
      return;
    }

    if (this.data.uploadItems) {
      this.data.uploadItems().subscribe((items) => {
        this.listaItems = items;
        this.options = items.map((i) => ({
          value: i.id,
          label: i.name,
        }));
      });
    }
  }

  // Busca el item completo por el value seleccionado.
  onSelectOption(opcion: OptionSelect): void {
    this.itemSelected = this.listaItems.find((i) => i.id === opcion.value) ?? null;
  }

  // Agrega el item. Si ya estaba seleccionado, le suma 1 a la cantidad.
  add(): void {
    const item = this.itemSelected;
    if (!item) return;

    const existente = this.items.find((i) => i.id === item.id);
    if (existente) {
      existente.qty += 1;
      this.items = [...this.items]; // nueva referencia, actualiza la tabla
    } else {
      this.items = [
        ...this.items,
        {
          ...item,
          qty: 1,
        },
      ];
    }

    this.itemSelected = null;
  }

  increase(item: ItemSelected): void {
    item.qty += 1;
  }

  decrease(item: ItemSelected): void {
    if (item.qty > 1) item.qty -= 1;
  }

  changeQty(item: ItemSelected, valor: string | number): void {
    const n = Math.floor(Number(valor));
    item.qty = Number.isFinite(n) && n > 0 ? n : 1;
  }

  eliminar(item: ItemSelected): void {
    this.items = this.items.filter((i) => i !== item);
  }

  //Total de una fila: valorUnitario * cantidad.
  totalItem(item: ItemSelected): number {
    return item.unitValue * item.qty;
  }

  // suma de todos los totales por item.
  get totalGeneral(): number {
    return this.items.reduce((acc, i) => acc + this.totalItem(i), 0);
  }

  increasePackage(item: ItemSelected): void {
    item.package = (item.package ?? 1) + 1;
  }

  decreasePackage(item: ItemSelected): void {
    if ((item.package ?? 1) > 1) {
      item.package = (item.package ?? 1) - 1;
    }
  }

  changePackage(item: ItemSelected, value: string | number): void {
    const n = Math.floor(Number(value));
    item.package = Number.isFinite(n) && n > 0 ? n : 1;
  }

  //Cierra y devuelve los items seleccionados.
  save(): void {
    this.dialogRef.close({
      items: this.items,
    });
  }

  changeValue(item: any, value: string) {
    const parsed = Number(value);
    item.unitValue = isNaN(parsed) || parsed < 0 ? 0 : parsed;
  }

  closeDialog(): void {
    this.dialogRef.close();
  }
}
